import { supabase } from '../config/supabaseClient';
import { API_BASE_URL } from '../config';

export interface DeliverabilityAlert {
  code: string;
  level: 'info' | 'warn' | 'crit';
  message: string;
  meta?: Record<string, any>;
}

export interface DeliverabilityAlertsResponse {
  since: string;
  mailbox_id?: string;
  campaign_id?: string;
  alerts: DeliverabilityAlert[];
}

export interface DomainHealthDomain {
  domain: string;
  sent: number;
  bounced: number;
  complaints: number;
  bounceRate: number;
  complaintRate: number;
  alerts: string[];
  status?: 'healthy' | 'watch' | 'blocked';
}

export interface DomainHealthResponse {
  window: {
    days: number;
    since: string;
    until: string;
  };
  totals: {
    sent: number;
    bounced: number;
    complaints: number;
    bounceRate: number;
    complaintRate: number;
  };
  domains: DomainHealthDomain[];
}

// Minimal, local copy for now. Consider refactor to shared api client.
const buildApiUrl = (path: string, params?: Record<string, string>): string => {
  const cleanPath = path.trim();
  const formattedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  const cleanBaseUrl = API_BASE_URL.trim();
  let url = `${cleanBaseUrl}${formattedPath}`;
  if (params && Object.keys(params).length > 0) {
    const queryString = Object.entries(params)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    url = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
  }
  return url;
};

const getToken = async () => {
  const sessionResp = await supabase.auth.getSession();
  return (sessionResp as any)?.data?.session?.access_token || '';
};

const fetchJSON = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const token = await getToken();
  const userResp = await supabase.auth.getUser();
  const userId = (userResp as any)?.data?.user?.id;
  if (!userId) throw new Error('User authentication required');

  const method = options.method || 'GET';
  if (method === 'GET') {
    url = url + (url.includes('?') ? '&' : '?') + `userId=${encodeURIComponent(userId)}`;
  }

  let body;
  if (method !== 'GET') {
    body = options.body ? JSON.stringify({ ...JSON.parse(options.body as string), userId }) : JSON.stringify({ userId });
  }

  const resp = await fetch(url, {
    ...options,
    method,
    headers: {
      ...options.headers,
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    ...(method !== 'GET' && { body }),
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `Request failed: ${resp.status}`);
  }
  return resp.json();
};

const deliverabilityService = {
  async getAlerts(params: { mailboxId?: string; campaignId?: string; sinceDays?: number } = {}): Promise<DeliverabilityAlertsResponse> {
    const { mailboxId, campaignId, sinceDays } = params;
    const qp: Record<string, string> = {};
    if (mailboxId) qp.mailboxId = mailboxId;
    if (campaignId) qp.campaignId = campaignId;
    if (sinceDays) qp.sinceDays = String(Math.max(1, Math.min(90, sinceDays)));
    const url = buildApiUrl('/api/email/deliverability/alerts', qp);
    return fetchJSON<DeliverabilityAlertsResponse>(url);
  },

  async getDomainHealth(params: { days?: number; maxDomains?: number } = {}): Promise<DomainHealthResponse> {
    const qp: Record<string, string> = {};
    if (params.days !== undefined) {
      const clampedDays = Math.max(1, Math.min(90, params.days));
      qp.days = String(clampedDays);
    }
    if (params.maxDomains !== undefined) {
      const clampedMax = Math.max(1, Math.min(200, params.maxDomains));
      qp.maxDomains = String(clampedMax);
    }
    const url = buildApiUrl('/api/email/dashboard/deliverability', qp);
    return fetchJSON<DomainHealthResponse>(url);
  },

  async simulateAlert(payload?: Partial<DeliverabilityAlert>): Promise<DeliverabilityAlert> {
    const url = buildApiUrl('/api/email/deliverability/alerts/test');
    return fetchJSON<DeliverabilityAlert>(url, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  },
};

export default deliverabilityService;
